import { useEffect, useState, useMemo, useCallback } from "react";
import { slurpApi, slurpWs } from "./slurpApi";
import { adaptSlurpTokens } from "./adaptToken";

/**
 * Central token-feed hook. REST-seeds from /api/tokens, then subscribes to
 * WS so new launches and trade-driven stat updates arrive live.
 *
 * The same shape is consumed by TokenDashboard, FeaturedTokens, DexTable —
 * one source of truth, zero client-side calculation.
 *
 * Returns:
 *   { tokens, isLoading, error, refresh, isLive }
 *
 * Options:
 *   sort: 'volume' | 'newest' | 'mcap' | 'change'
 *   limit, offset, search
 */
export function useTokenFeed({ sort = "volume", limit = 100, offset = 0, search = "" } = {}) {
  const [tokens, setTokens] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLive, setIsLive] = useState(false);

  const fetchNow = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await slurpApi.listTokens({ sort, limit, offset, search });
      setTokens(res.tokens ?? []);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [sort, limit, offset, search]);

  useEffect(() => {
    void fetchNow();
  }, [fetchNow]);

  // Live updates.
  useEffect(() => {
    if (!slurpWs) return undefined;
    return slurpWs.subscribe((msg) => {
      if (msg?.type === "__status") {
        setIsLive(msg.status === "connected");
        return;
      }
      if (msg?.type === "token:new") {
        setTokens((prev) => {
          if (prev.find((t) => t.mint === msg.token.mint)) return prev;
          return [msg.token, ...prev];
        });
      } else if (msg?.type === "token:update") {
        setTokens((prev) =>
          prev.map((t) => (t.mint === msg.token.mint ? { ...t, ...msg.token } : t))
        );
      } else if (msg?.type === "trade:new") {
        // Partial update from the trade's associated token stats + append
        // the new price point to the running sparkline. Stamp last_trade_at
        // so the dashboard's "Recent Trades" sort sees the activity.
        const tradePrice = Number(msg.trade?.price_usd);
        const tradeTs = msg.trade?.ts || new Date().toISOString();
        const tradedMint = msg.token?.mint;
        setTokens((prev) => {
          const exists = prev.some((t) => t.mint === tradedMint);
          if (!exists) {
            // Token isn't in our window yet — pull the full row so the bump
            // can render. We append a placeholder immediately and let the
            // fetch backfill the rest; the WS update has only stat fields.
            if (tradedMint) {
              slurpApi
                .getToken(tradedMint)
                .then((row) => {
                  if (!row) return;
                  setTokens((cur) => {
                    if (cur.some((t) => t.mint === row.mint)) return cur;
                    // Stamp both column names so the adapter's Bumps key
                    // picks `last_traded_at` regardless of which value
                    // the backend race left in the row.
                    return [{ ...row, last_traded_at: tradeTs, last_trade_at: tradeTs, updated_at: tradeTs }, ...cur];
                  });
                })
                .catch(() => {});
            }
            return prev;
          }
          return prev.map((t) =>
            t.mint === tradedMint
              ? {
                  ...t,
                  volume_usd_24h: msg.token.volume_usd_24h,
                  last_price_usd: msg.token.last_price_usd,
                  market_cap_usd: msg.token.market_cap_usd,
                  price_change_24h: msg.token.price_change_24h,
                  // Stamp BOTH field names — `last_traded_at` is the
                  // canonical backend column (what the adapter reads for
                  // Bumps sort); `last_trade_at` is the older in-memory
                  // alias kept for any consumer still on the old name.
                  last_traded_at: tradeTs,
                  last_trade_at: tradeTs,
                  updated_at: tradeTs,
                  sparkline: Number.isFinite(tradePrice) && tradePrice > 0
                    ? [...(Array.isArray(t.sparkline) ? t.sparkline : []), tradePrice].slice(-30)
                    : t.sparkline,
                }
              : t
          );
        });
      }
    });
  }, []);

  // Convenience projections — each component can pick what it needs without re-sorting.
  const byNewest = useMemo(
    () => [...tokens].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [tokens]
  );
  const byVolume = useMemo(
    () => [...tokens].sort((a, b) => Number(b.volume_usd_24h || 0) - Number(a.volume_usd_24h || 0)),
    [tokens]
  );
  const byMcap = useMemo(
    () => [...tokens].sort((a, b) => Number(b.market_cap_usd || 0) - Number(a.market_cap_usd || 0)),
    [tokens]
  );
  const byChange = useMemo(
    () => [...tokens].sort((a, b) => Number(b.price_change_24h || 0) - Number(a.price_change_24h || 0)),
    [tokens]
  );

  // Legacy UI shape — what PremiumCard / DexTable / FeaturedRow consume.
  const uiTokens = useMemo(() => adaptSlurpTokens(tokens), [tokens]);

  return {
    tokens,
    uiTokens,
    byNewest,
    byVolume,
    byMcap,
    byChange,
    isLoading,
    error,
    isLive,
    refresh: fetchNow,
  };
}
